import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../lib/api";
import { ArrowLeft, Eye, Tag, Calendar, User2 } from "lucide-react";

/** Very small, dependency-free Markdown renderer for the blog body.
 * Supports: H1/H2/H3, bold, italic, links, lists, code, blockquote, hr, paragraphs.
 * Good enough for our editorial content — keeps the bundle small. */
function renderMarkdown(md = "") {
    const escapeHtml = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    let s = escapeHtml(md);
    // Code blocks
    s = s.replace(/```([\s\S]*?)```/g, (_, c) => `<pre class="bg-stone-900 text-stone-100 p-4 overflow-x-auto text-xs my-3"><code>${c}</code></pre>`);
    // Inline code
    s = s.replace(/`([^`]+)`/g, '<code class="bg-stone-100 px-1.5 py-0.5 text-xs">$1</code>');
    // Headings
    s = s.replace(/^### (.*)$/gm, '<h3 class="font-serif text-2xl text-stone-900 mt-8 mb-3">$1</h3>');
    s = s.replace(/^## (.*)$/gm, '<h2 class="font-serif text-3xl text-stone-900 mt-10 mb-4">$1</h2>');
    s = s.replace(/^# (.*)$/gm, '<h1 class="font-serif text-4xl text-stone-900 mt-12 mb-5">$1</h1>');
    // Bold, italic
    s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    s = s.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em>$1</em>');
    // Links [text](url)
    s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-sky-700 hover:underline" target="_blank" rel="noopener">$1</a>');
    // Horizontal rule
    s = s.replace(/^---$/gm, '<hr class="border-t border-stone-200 my-8" />');
    // Blockquote
    s = s.replace(/^> (.*)$/gm, '<blockquote class="border-l-4 border-sky-700 pl-4 italic text-stone-600 my-4">$1</blockquote>');
    // Unordered lists
    s = s.replace(/(^- .*(?:\n- .*)*)/gm, (block) => {
        const items = block.split("\n").map(l => l.replace(/^- /, "")).map(l => `<li class="ml-1">${l}</li>`).join("");
        return `<ul class="list-disc pl-6 my-3 space-y-1">${items}</ul>`;
    });
    // Paragraphs (blank-line separated)
    s = s.split(/\n{2,}/).map(p => {
        if (/^\s*<(h1|h2|h3|ul|pre|hr|blockquote)/.test(p)) return p;
        return `<p class="leading-relaxed text-stone-800 my-4">${p.replace(/\n/g, "<br/>")}</p>`;
    }).join("\n");
    return s;
}

export default function BlogPost() {
    const { slug } = useParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");

    useEffect(() => {
        api.get(`/blog/${slug}`).then(r => {
            setData(r.data);
            // Set SEO meta in-place (simple, no helmet dep)
            const post = r.data.post || {};
            if (post.seo_title) document.title = post.seo_title;
            else if (post.title) document.title = `${post.title} · Hahnemann Journal`;
            const setMeta = (name, content) => {
                if (!content) return;
                let el = document.querySelector(`meta[name="${name}"]`);
                if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
                el.setAttribute("content", content);
            };
            setMeta("description", post.seo_description || post.excerpt || "");
            setMeta("keywords", post.seo_keywords || (post.tags || []).join(", "));
            const setOg = (prop, content) => {
                if (!content) return;
                let el = document.querySelector(`meta[property="${prop}"]`);
                if (!el) { el = document.createElement("meta"); el.setAttribute("property", prop); document.head.appendChild(el); }
                el.setAttribute("content", content);
            };
            setOg("og:title", post.seo_title || post.title);
            setOg("og:description", post.seo_description || post.excerpt || "");
            setOg("og:image", post.cover_url || "");
            setOg("og:type", "article");
        }).catch(e => setErr(e.response?.data?.detail || "Couldn't load article"));
    }, [slug]);

    if (err) return (
        <div className="max-w-xl mx-auto p-12 text-center">
            <p className="text-sm text-red-700">{err}</p>
            <Link to="/blog" className="text-sky-700 hover:underline text-sm mt-3 inline-block">Back to all articles</Link>
        </div>
    );
    if (!data) return <div className="p-12 text-center text-sm text-stone-500">Loading…</div>;

    const post = data.post;
    return (
        <article className="bg-white" data-testid={`blog-post-${slug}`}>
            <div className="max-w-3xl mx-auto px-4 pt-10">
                <Link to="/blog" className="text-xs text-sky-700 hover:underline inline-flex items-center gap-1 mb-6" data-testid="blog-back"><ArrowLeft className="w-3 h-3" />Hahnemann Journal</Link>
                {post.category && <div className="label-eyebrow text-sky-700">{post.category}</div>}
                <h1 className="font-serif text-4xl sm:text-5xl text-stone-900 leading-tight mt-2" data-testid="blog-title">{post.title}</h1>
                <div className="flex items-center gap-3 mt-3 text-xs text-stone-500 flex-wrap">
                    <span className="inline-flex items-center gap-1"><User2 className="w-3 h-3" />{post.author_name}</span>
                    <span>·</span>
                    <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{(post.published_at || "").slice(0, 10)}</span>
                    {post.views > 0 && <><span>·</span><span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" />{post.views} reads</span></>}
                </div>
                {post.cover_url && (
                    <div className="aspect-[16/9] bg-stone-100 mt-6 overflow-hidden">
                        <img src={post.cover_url} alt={post.title} className="w-full h-full object-cover" />
                    </div>
                )}
                <div className="prose max-w-none mt-6" dangerouslySetInnerHTML={{ __html: renderMarkdown(post.body_md) }} />

                {post.tags?.length > 0 && (
                    <div className="flex items-center gap-2 mt-10 flex-wrap">
                        <Tag className="w-3 h-3 text-stone-400" />
                        {post.tags.map(t => (
                            <span key={t} className="text-[10px] uppercase tracking-widest px-2 py-1 bg-stone-100 text-stone-600">{t}</span>
                        ))}
                    </div>
                )}

                {data.related?.length > 0 && (
                    <section className="border-t border-stone-200 mt-12 pt-8">
                        <h2 className="font-serif text-2xl text-stone-900 mb-4">More from {post.category || "the journal"}</h2>
                        <div className="grid sm:grid-cols-3 gap-4">
                            {data.related.map(r => (
                                <Link key={r.id} to={`/blog/${r.slug}`} className="block border border-stone-200 p-4 hover:border-sky-300">
                                    <div className="font-serif text-lg text-stone-900 leading-snug">{r.title}</div>
                                    {r.excerpt && <p className="text-xs text-stone-500 mt-1 line-clamp-2">{r.excerpt}</p>}
                                </Link>
                            ))}
                        </div>
                    </section>
                )}
            </div>
            <div className="h-16" />
        </article>
    );
}
